import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { useSaveDateDayCalendarMutation } from "../../store/services/CalendarHoursService";
import Loader from "./Loader";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useGetSediVisibiliQuery } from "../../store/services/CalendarHoursDaysService";

export interface AppointmentSede {
    sede: number;
    limiteAppuntamenti: number | null;
    limiteSimultaneiMattina: number | null;
    limiteSimultaneiPomeriggio: number | null;
}

interface PropsCalendarDialog {
    selectedDay: any;
    currentDate: any;
    setShowModal: (val: boolean) => void;
    appointments: { [key: string]: AppointmentSede };
    sedi: any[];
    globalAppointments: any;
}

export default function CalendarDialog({
    selectedDay,
    currentDate,
    setShowModal,
    sedi,
    appointments,
    globalAppointments
}: PropsCalendarDialog) {

    const form = useForm();
    const {
        handleSubmit,
        reset,
        register,
        formState: { dirtyFields },
    } = form;

    const [saveDateDayCalendar, { isLoading }] =
        useSaveDateDayCalendarMutation();

    const { data: sediVisibiliData, isLoading: sediLoading } =
        useGetSediVisibiliQuery(undefined);

    // Evita re-render / reset del form se il parent passa un nuovo riferimento dell'array sedi con le stesse chiavi
    const sediKeyString = JSON.stringify(sedi?.map(s => s.chiave));

    const memoizedSedi = useMemo(() => {
        return sedi || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sediKeyString]);

    // Filtra dinamicamente le sedi in base ai permessi dal backend
    const activeSedi = useMemo(() => {
        if (!memoizedSedi) return [];
        if (!sediVisibiliData?.result) {
            return memoizedSedi;
        }
        return memoizedSedi.filter(sede => sediVisibiliData.result[sede.chiave] === true);
    }, [memoizedSedi, sediVisibiliData]);

    /** 🔹 RESET VALORI DAL BACKEND */
    const selectedDayStr = selectedDay?.dateStr;

    useEffect(() => {
        if (!selectedDayStr || !appointments || !activeSedi.length) return;

        const defaultValues: any = {};

        activeSedi.forEach((sede) => {
            const sedeData: AppointmentSede | undefined =
                appointments[sede.chiave];

            defaultValues[sede.chiave] = {
                limiteAppuntamenti: sedeData?.limiteAppuntamenti ?? null,
                limiteSimultaneiMattina:
                    sedeData?.limiteSimultaneiMattina ?? null,
                limiteSimultaneiPomeriggio:
                    sedeData?.limiteSimultaneiPomeriggio ?? null,
            };
        });

        reset(defaultValues);
    }, [selectedDayStr, appointments, activeSedi, reset]);

    /** 🔹 SAVE */
    const toNumberOrNull = (v: any) => {
        const n = Number(v);
        return v === "" || v === undefined || Number.isNaN(n) ? null : n;
    };

    const handleSaveInfo = (data: any) => {
        const dataSave: any = {
            data: selectedDay.dateStr,
        };

        activeSedi.forEach((sede) => {
            const sedeDirty = dirtyFields?.[sede.chiave];
            if (!sedeDirty) return; // sede non toccata → NON la mando

            const sedeData = data[sede.chiave];
            const sedeOriginal = appointments[sede.chiave]; // valori backend

            dataSave[sede.chiave] = {
                limiteAppuntamenti: sedeDirty.limiteAppuntamenti
                    ? toNumberOrNull(sedeData.limiteAppuntamenti)
                    : sedeOriginal?.limiteAppuntamenti ?? null,

                limiteSimultaneiMattina: sedeDirty.limiteSimultaneiMattina
                    ? toNumberOrNull(sedeData.limiteSimultaneiMattina)
                    : sedeOriginal?.limiteSimultaneiMattina ?? null,

                limiteSimultaneiPomeriggio: sedeDirty.limiteSimultaneiPomeriggio
                    ? toNumberOrNull(sedeData.limiteSimultaneiPomeriggio)
                    : sedeOriginal?.limiteSimultaneiPomeriggio ?? null,
            };
        });

        if (Object.keys(dataSave).length === 1) {
            toast.info("Nessuna modifica da salvare");
            return;
        }

        saveDateDayCalendar(dataSave)
            .unwrap()
            .then(() => {
                setShowModal(false);
                toast.success("Numero appuntamenti aggiornato");
            })
            .catch(() => {
                toast.error("Errore durante il salvataggio");
            });
    };


    return (
        <form
            onSubmit={handleSubmit(handleSaveInfo)}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
            {(isLoading || sediLoading) && <Loader />}

            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* HEADER */}
                <div className="bg-[#064e3b] text-white p-6 rounded-t-lg flex items-center justify-between sticky top-0">
                    <div>
                        <h2 className="text-2xl font-bold">
                            {selectedDay.day}{" "}
                            {currentDate.toLocaleDateString("it-IT", {
                                month: "long",
                                year: "numeric",
                            })}
                        </h2>
                        <p className="text-blue-100 text-sm mt-1">
                            {new Date(
                                currentDate.getFullYear(),
                                currentDate.getMonth(),
                                selectedDay.day
                            ).toLocaleDateString("it-IT", {
                                weekday: "long",
                            })}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="h-10 bg-[#064e3b] text-white px-2 inline rounded-lg"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* CONTENUTO */}
                <div className="p-6">
                    <h3 className="text-lg text-gray-800 mb-4 text-center">
                        Gestione numero massimo appuntamenti per sede
                    </h3>

                    <div className="space-y-4">
                        {activeSedi.map((sede) => {
                            const limitiGlobali = globalAppointments?.[sede.chiave];

                            return (
                                <div key={sede.id} className={`border-2 ${sede.coloreBordo} rounded-lg p-4`}>
                                    {/* HEADER SEDE */}
                                    <div className="flex items-center mb-3 gap-1">
                                        <div className="flex items-center gap-2">
                                            <div className={`${sede.colore} w-4 h-4 rounded-full`} />
                                            <h4 className="font-semibold text-gray-800">{sede.nome}</h4>
                                        </div>
                                        <span className="text-gray-400 text-sm">- Limite globale: {sede.maxOre}</span>
                                    </div>

                                    {/* INPUT */}
                                    <div className="grid grid-cols-3 gap-4 items-end">
                                        <div className="flex flex-col">
                                            <label className="text-sm text-gray-600">Appuntamenti</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                {...register(`${sede.chiave}.limiteAppuntamenti`)}
                                                className="border rounded px-2 py-1 text-sm"
                                                placeholder={`Appuntamenti${limitiGlobali?.limiteAppuntamenti != null
                                                        ? ` (${limitiGlobali.limiteAppuntamenti})`
                                                        : ""
                                                    }`}
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm text-gray-600">App. simult. mattina</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                {...register(`${sede.chiave}.limiteSimultaneiMattina`)}
                                                className="border rounded px-2 py-1 text-sm"
                                                placeholder={`Simultanei mattina${limitiGlobali?.limiteSimultaneiMattina != null
                                                        ? ` (${limitiGlobali.limiteSimultaneiMattina})`
                                                        : ""
                                                    }`}
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm text-gray-600">App. simult. pomeriggio</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                {...register(`${sede.chiave}.limiteSimultaneiPomeriggio`)}
                                                className="border rounded px-2 py-1 text-sm"
                                                placeholder={`Simultanei pomeriggio${limitiGlobali?.limiteSimultaneiPomeriggio != null
                                                        ? ` (${limitiGlobali.limiteSimultaneiPomeriggio})`
                                                        : ""
                                                    }`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                </div>

                {/* FOOTER */}
                <div className="bg-gray-50 p-4 rounded-b-lg border-t border-gray-200 flex gap-3 sticky bottom-0">
                    <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition"
                    >
                        Chiudi
                    </button>

                    <button
                        type="submit"
                        className="flex-1 h-10 bg-[#064e3b] text-white px-2 inline rounded-lg"
                    >
                        Salva
                    </button>
                </div>
            </div>
        </form>
    );
}